"use client";

import { X } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, useEffect, useState } from "react";

const TEASER_DELAY_MS = 3000;

// Storage can throw (privacy mode): the teaser then just shows again.
function readStored(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function writeStored(storage: () => Storage, key: string, value: string): void {
  try {
    storage().setItem(key, value);
  } catch {
    // Not remembered; harmless.
  }
}

const local = (): Storage => window.localStorage;
const session = (): Storage => window.sessionStorage;

/** Once per browser session; closed or chat opened = gone until the text changes. Same rules as `public/embed/chat.js`. */
function teaserWanted(storageKey: string, text: string): boolean {
  return readStored(local, storageKey) !== text && readStored(session, storageKey) !== text;
}

export function rememberTeaserDismissed(storageKey: string, text: string | null): void {
  if (text) writeStored(local, storageKey, text);
}

/** Line popping out of the visitor bubble a few seconds after load; click opens the chat. */
export function MessengerTeaser({
  text,
  storageKey,
  onOpen,
  onShown,
}: {
  readonly text: string;
  readonly storageKey: string;
  readonly onOpen: () => void;
  readonly onShown: () => void;
}): ReactElement | null {
  const t = useTranslations("messenger.launcher");
  const [shown, setShown] = useState<boolean>(false);

  useEffect(() => {
    if (!teaserWanted(storageKey, text)) return;
    const timer = window.setTimeout(() => {
      writeStored(session, storageKey, text);
      setShown(true);
      onShown();
    }, TEASER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [onShown, storageKey, text]);

  if (!shown) return null;
  return (
    <div className="group fixed bottom-[5.625rem] right-5 z-[1050] max-w-[min(280px,calc(100vw-40px))] origin-bottom-right animate-in fade-in-0 zoom-in-90 slide-in-from-bottom-2 duration-300 print:hidden">
      <button
        type="button"
        onClick={onOpen}
        className="block w-full rounded-2xl border border-black/[0.06] bg-background px-4 py-3 text-left text-[14px] leading-relaxed text-primary shadow-[0_10px_32px_rgba(0,0,0,0.14)]"
      >
        {text}
      </button>
      <button
        type="button"
        aria-label={t("hinweisSchliessen")}
        onClick={() => {
          rememberTeaserDismissed(storageKey, text);
          setShown(false);
        }}
        className="absolute -right-1.5 -top-2 flex h-[22px] w-[22px] items-center justify-center rounded-full border border-black/[0.08] bg-background text-primary/60 shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-opacity hover:text-primary [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:focus-visible:opacity-100 [@media(hover:hover)]:group-hover:opacity-100"
      >
        <X className="h-3 w-3" strokeWidth={2.6} aria-hidden />
      </button>
    </div>
  );
}
